import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faTimes } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { formatPriceCurrency, getCurrency, openInNewWindow, showSuccess } from 'utils/utils';
import useModalClass from 'hooks/useModalClass';
import { MyOrder } from 'ts/shared/interfaces/MOResponse';
import { createCustomPaymentBlock, CreateCustomPaymentBlockResponse, createCustomPaymentLink, updatePaymentBlock } from 'services/myOrders';
import { getStatus } from 'constants/mo';
import withErrorBoundary from '../withErrorBoundary';
import { MainContext } from 'contexts/MainContext';

interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: MyOrder;
}

const AddPaymentModal: FC<AddPaymentModalProps> = ({ isOpen, onClose, order }) => {
  const { t } = useTranslation();
  const classList = useModalClass(isOpen);
  const [paymentBlock, setPaymentBlock] = useState<CreateCustomPaymentBlockResponse | null>(null);
  const [numberOfPayments, setNumberOfPayments] = useState<string>('1');
  const [error, setError] = useState<string>('');
  const [commissionValue, setCommissionValue] = useState<string>('');
  const [addedCommission, setAddedCommission] = useState<number>(0);
  const [paymentLink, setPaymentLink] = useState<string>('');
  const { user } = useContext(MainContext);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setPaymentBlock(null);
      setNumberOfPayments('1');
      setCommissionValue('');
      setAddedCommission(0);
      setPaymentLink('');
      (async () => {
        const res = await createCustomPaymentBlock({
          orderId: order.fullSegment.orderId,
          segmentId: order.fullSegment.orderSegId,
        });
        if (res?.credit2000PaymentInfo || res?.isRevolut) {
          setPaymentBlock(res);
        } else {
          setError('Failed to create payment block');
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onClickCreatePayment = async (type?: number) => {
    let revolutCurrency = '';
    if (type) {
      if ([1, 3].includes(type)) {
        revolutCurrency = paymentBlock?.revolutPaymentsLevel1?.currency || '';
      } else if ([2, 4].includes(type)) {
        revolutCurrency = paymentBlock?.revolutPaymentsLevel2?.currency || '';
      }
    }
    let params: any = {
      orderId: order.fullSegment.orderId,
      segmentId: order.fullSegment.orderSegId,
      couponAmount: parseInt(commissionValue) || 0,
      couponCurrency: revolutCurrency || order.orderRow.currency,
    }
    if (paymentBlock?.credit2000PaymentInfo) {
      params.paymentSettings = {
        ...paymentBlock?.credit2000PaymentInfo,
        numberOfRegularPayments: parseInt(numberOfPayments),
      }
    }
    if (type) {
      params.revolutPaymentLinkType = type;
    }
    const res = await createCustomPaymentLink(params);

    if (res?.transactionId && res?.paymentUrl) {
      const params = new URLSearchParams(window.location.search);
      params.set('transactionId', res.transactionId);
      params.set('paymentUrl', res.paymentUrl);
      const link = `${window.location.origin}/my-orders/payment?${params.toString()}`;
      setPaymentLink(link);
    }
  };

  const onClickAddCommission = async () => {
    setAddedCommission(parseInt(commissionValue) || 0);
    const newPaymentBlock = await updatePaymentBlock({
      packageCurrency: order.orderRow.currency,
      sourcePrice: Math.round(paymentBlock?.credit2000PaymentInfo?.fullPriceInShekel || 0),
      addPrice: parseInt(commissionValue) || 0,
      searchToken: order.fullSegment.orderId + ''
    });

    if (newPaymentBlock?.credit2000PaymentInfo) {
      setPaymentBlock(newPaymentBlock);
    } else {
      setError('Failed to add commission');
    }
  };

  return (
    <Fragment>
      {isOpen &&
        createPortal(
          <div
            className="modal-overlay modal-overlay-top"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <div className={classList}>
              <div className="modal-title ">
                <div>
                  <span className="title mr-10">
                    {t('mo.order_report')}
                    {order?.fullSegment && (
                      <Fragment>
                        <span className="mr-5">{' '}-{' '}Order: {order.orderRow.orderId}</span>
                        <span className="mr-5">Segment: {order.fullSegment.orderSegId}</span>
                        {!!order.orderRow.status && (
                          <span className={`mo-status ${getStatus(order.orderRow.status)}`}>
                            {order.orderRow.status}
                          </span>
                        )}
                      </Fragment>
                    )}
                  </span>
                </div>
                <div>
                  <FontAwesomeIcon icon={faTimes} onClick={() => onClose()} />
                </div>
              </div>

              <div>
                <div className="default-shadow details-block">
                  <div className="title title-header-grey text-md mb-20 mt-10">
                    <FontAwesomeIcon
                      icon={faCreditCard}
                      className="mr-5 text-sm text-contrast"
                    />
                    <span className="mr-5">{t('passenger.fast_secure')}</span>
                  </div>
                  {!error && paymentBlock?.credit2000PaymentInfo && (
                    <div className="form-wrapper row">
                      <div className="col-md-9 col-12">
                        <div className="form-group">
                          <div className="mb-5">
                            {t('passenger.number_of_payments')}
                          </div>
                          <SelectCustom
                            options={paymentBlock?.credit2000PaymentInfo?.listOfRegularPayments?.map((payment: any) => ({
                              value: payment.value,
                              label: payment.text,
                            }))}
                            onChange={(value: string) => setNumberOfPayments(value)}
                            value={numberOfPayments}
                            appendToBody
                          />
                        </div>
                      </div>
                      <div className="col-12 flex-row">
                        <Button
                          className="btn btn-primary btn-green"
                          title={'Create Payment Link'}
                          onClick={() => onClickCreatePayment()}
                        />

                        {paymentLink && <Fragment>
                          <Button
                            className='btn btn-primary ml-10'
                            title={'Open in new tab'}
                            onClick={() => openInNewWindow(paymentLink)}
                          />
                          <Button
                            className='btn btn-default ml-10'
                            title={'Copy Link'}
                            onClick={() => { navigator.clipboard.writeText(paymentLink); showSuccess(t('mo.clipboard_success')) }}
                          />
                        </Fragment>
                        }
                      </div>
                    </div>
                  )}
                  {!error && paymentBlock?.isRevolut && (
                    <div>
                      <div className="form-wrapper row">
                        <div className="col-md-6 col-12">
                          {t('passenger.procces_with_revolut')}{' '}
                          {user?.showLLV && <span>{t('passenger.as_client')}</span>}
                        </div>
                        <div className="col-md-3 col-12">
                          {paymentBlock?.revolutPaymentsLevel1?.secondPrice && (
                            <Button
                              className="btn btn-primary btn-green"
                              title={'Create Payment Link ' + formatPriceCurrency(
                                paymentBlock?.revolutPaymentsLevel1?.secondPrice || 0,
                                getCurrency(paymentBlock?.revolutPaymentsLevel1?.secondCurrency || '')
                              )}
                              onClick={() => onClickCreatePayment(2)}
                            />
                          )}
                        </div>
                        <div className="col-md-3 col-12">
                          <Button
                            className="btn btn-primary btn-green"
                            title={'Create Payment Link ' + formatPriceCurrency(
                              paymentBlock?.revolutPaymentsLevel1?.price || 0,
                              getCurrency(paymentBlock?.revolutPaymentsLevel1?.currency || '')
                            )}
                            onClick={() => onClickCreatePayment(1)}
                          />
                        </div>
                      </div>
                      {user?.showLLV && <div className="form-wrapper row">
                        <div className="col-12">
                          <div className="divider mb-10 mt-10"></div>
                        </div>
                        <div className="col-md-6 col-12">
                          <span>{t('passenger.as_agent')}</span>
                        </div>
                        <div className="col-md-3 col-12">
                          {paymentBlock?.revolutPaymentsLevel2?.secondPrice && (
                            <Button
                              className="btn btn-primary btn-primary"
                              title={'Create Payment Link'}
                              onClick={() => onClickCreatePayment(4)}
                            />
                          )}
                        </div>
                        <div className="col-md-3 col-12">
                          <Button
                            className="btn btn-primary btn-primary"
                            title={'Create Payment Link'}
                            onClick={() => onClickCreatePayment(3)}
                          />
                        </div>
                      </div>}

                      {paymentLink && <div className="col-12 flex-row">
                        <div className="col-12 col-md-6">
                          <Button
                            className='btn btn-primary ml-10'
                            title={'Open in new tab'}
                            onClick={() => openInNewWindow(paymentLink)}
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <Button
                            className='btn btn-default ml-10'
                            title={'Copy Link'}
                            onClick={() => { navigator.clipboard.writeText(paymentLink); showSuccess(t('mo.clipboard_success')) }}
                          />
                        </div>
                      </div>
                      }

                    </div>
                  )}
                  {error && <div className="alert alert-danger">{error}</div>}
                </div>

                {!error && paymentBlock && (
                  <div className="default-shadow details-block">
                    <div className="title text-md title-header-grey  mb-10">
                      {t('hotel.sales_price')}
                    </div>
                    <div className="display-flex flex-wrap">
                      {!paymentBlock?.isRevolut && <div className="mr-10" style={{ width: 40 }}>
                        <SelectCustom
                          options={[
                            {
                              value: order.orderRow.currency,
                              label: getCurrency(order.orderRow.currency),
                            },
                          ]}
                          value={order.orderRow.currency}
                          disabled
                          onChange={() => { }}
                        />
                      </div>
                      }

                      <div style={{ marginTop: -5, flex: '1 0 calc(100% - 200px)' }}>
                        <FormGroup
                          value={commissionValue}
                          onChange={setCommissionValue}
                          label=""
                          allowOnlyNumbers
                        />
                      </div>
                      {!paymentBlock?.isRevolut && (
                        <Button
                          className="ml-10 btn btn-primary mb-15"
                          title={t('mo.add_comission')}
                          onClick={onClickAddCommission}
                        />
                      )}
                    </div>
                    {!paymentBlock?.isRevolut && (
                      <div className="book-summary-total">
                        <span className="title text-lg">{t('total')}: <span className="text-lg mr-5">
                          {formatPriceCurrency(
                            (order.orderRow.price) + addedCommission,
                            getCurrency(order.orderRow.currency)
                          )}
                        </span></span>
                        <span>

                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <Button
                  className="btn btn-default"
                  title={t('close')}
                  onClick={() => onClose()}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </Fragment>
  );
};

export default withErrorBoundary(AddPaymentModal, 'AddPaymentModal');